import { useState, type ReactNode } from "react";
import type { Book } from "../api/types";
import { formatCost, formatDate, formatDuration, formatNumber, formatUploadDate } from "../lib/format";
import { ChevronDownIcon } from "./Icon";

interface Row {
  label: string;
  value: ReactNode;
  wrap?: boolean;
}

export default function MetadataPanel({ book }: { book: Book }) {
  const [open, setOpen] = useState(false);

  const rows: Row[] = [
    {
      label: "Source",
      wrap: true,
      value: (
        <a
          href={book.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent underline decoration-border underline-offset-2 hover:decoration-accent"
        >
          {book.url}
        </a>
      ),
    },
    { label: "Channel", value: book.channel ?? "-" },
    { label: "Published", value: formatUploadDate(book.upload_date) },
    { label: "Duration", value: formatDuration(book.duration_s) },
    { label: "Language", value: book.language ?? book.video_lang ?? "-" },
    { label: "Writer model", value: book.model_writer ?? "-" },
    { label: "Verifier model", value: book.model_verifier ?? "-" },
    { label: "Tokens (in / out)", value: `${formatNumber(book.tokens_input)} / ${formatNumber(book.tokens_output)}` },
    { label: "Cost", value: formatCost(book.cost_usd, book.cost_is_estimate) },
    { label: "Generated", value: formatDate(book.completed_at) },
  ];

  return (
    <div className="rounded-lg border border-border bg-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-text"
        aria-expanded={open}
      >
        <span>Details</span>
        <ChevronDownIcon width={16} height={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 border-t border-border px-4 py-4 text-sm sm:grid-cols-2">
          {rows.map((row) => (
            <div key={row.label} className="flex min-w-0 flex-col gap-0.5">
              <dt className="text-xs uppercase tracking-wide text-muted">{row.label}</dt>
              <dd className={row.wrap ? "break-all text-text" : "truncate text-text"}>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}
